import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useEffect, useMemo, useState, type JSX } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { ColumnDef } from '@tanstack/react-table';
import clsx from 'clsx';
import { Inbox, MoreHorizontal, Pencil } from 'lucide-react';
import { apiRequest, ApiError, type ListResponse } from '../lib/api';
import { useToast } from '../lib/toast';
import { useAuthStore } from '../lib/auth-store';
import { useConfirm } from '../components/ui/Confirm';
import { ListShell } from '../components/ListShell';
import { ListOrOutlet } from '../components/shared/ListOrOutlet';
import { Button } from '../components/ui/Button';
import { DataTable } from '../components/ui/DataTable';
import { EmptyState } from '../components/ui/EmptyState';
import { ErrorState } from '../components/ui/ErrorState';
import { Pagination } from '../components/people/Pagination';
import { formatINR, timeAgo, formatDate } from '../lib/format';
import { ClaimStatusBadge } from '../components/finance/StatusBadge';
import { ClaimEditDrawer } from '../components/finance/ClaimEditDrawer';
import { ClaimRejectDrawer } from '../components/finance/ClaimRejectDrawer';
import type { FeeClaimJoined, FeePaymentClaim } from '../components/finance/types';
import {
  FilterBar,
  SortableHeader,
  useListFilters,
  type FilterRoute,
} from '../components/filters';
import {
  claimsFilterConfig,
  claimsSearchSchema,
  CLAIMS_SORT_COLUMNS,
} from '../components/finance/claimsFilterConfig';

const PAGE_SIZE = 20;

const VIA_LABELS: Record<string, string> = {
  cash: 'Cash',
  bank_transfer: 'Bank transfer',
  upi_offline: 'UPI (offline)',
  cheque: 'Cheque',
  other: 'Other',
};

const ADMIN_ROLES = ['super_admin', 'finance_admin'] as const;

export const Route = createFileRoute('/_authed/finance/fees/claims')({
  validateSearch: claimsSearchSchema,
  component: ClaimsPage,
});

function ClaimsPage(): JSX.Element {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { confirm } = useConfirm();
  const role = useAuthStore((s) => s.user?.primaryRole ?? null);
  const isAdmin = role !== null && (ADMIN_ROLES as readonly string[]).includes(role);

  const [editClaim, setEditClaim] = useState<FeePaymentClaim | null>(null);
  const [rejectTarget, setRejectTarget] = useState<
    { id: string; studentName: string } | null
  >(null);

  // The single source of truth for filters / sort / page, backed by the route's
  // URL search params. The adapter narrows the route's precisely-typed
  // search/navigate surface to the toolkit's structural `FilterRoute` contract;
  // the route's own Zod `validateSearch` keeps the boundary typed.
  const filterRoute: FilterRoute = {
    useSearch: () => Route.useSearch() as Record<string, unknown>,
    useNavigate: () => {
      const navigateRoute = Route.useNavigate();
      return (opts) =>
        navigateRoute({
          search: ((prev: Record<string, unknown>) => opts.search(prev)) as never,
          ...(opts.replace !== undefined ? { replace: opts.replace } : {}),
        });
    },
  };
  const filters = useListFilters(claimsFilterConfig, filterRoute, { pageSize: PAGE_SIZE });
  const { values, apiParams, setSort, setPage } = filters;

  const claimsQuery = useQuery({
    queryKey: ['feeClaims', apiParams] as const,
    queryFn: ({ signal }) =>
      apiRequest<ListResponse<FeeClaimJoined>>('/api/fees/claims', {
        signal,
        query: apiParams,
      }),
    placeholderData: (prev) => prev,
  });

  const confirmMutation = useMutation({
    mutationFn: async (claimId: string) => {
      await apiRequest<{ ok: true }>(
        `/api/fees/claims/${encodeURIComponent(claimId)}/confirm`,
        { method: 'POST' },
      );
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['feeClaims'] });
      await queryClient.invalidateQueries({ queryKey: ['feePayments'] });
      toast('Claim confirmed and payment marked as paid.', { variant: 'success' });
    },
    onError: (err) => {
      if (err instanceof ApiError) toast(err.message, { variant: 'error' });
      else toast('Could not confirm claim.', { variant: 'error' });
    },
  });

  const onConfirmClick = async (claim: FeeClaimJoined): Promise<void> => {
    const ok = await confirm({
      title: 'Confirm reported payment?',
      description: `Mark this payment of ${formatINR(claim.payment.netAmountPaise)} from ${
        claim.student.name
      } as received. A receipt will be issued.`,
      confirmLabel: 'Confirm payment',
    });
    if (ok) confirmMutation.mutate(claim.claim.id);
  };

  const columns = useMemo<ColumnDef<FeeClaimJoined, unknown>[]>(
    () => [
      {
        id: 'claimedAt',
        accessorFn: (r) => r.claim.createdAt,
        enableSorting: false,
        header: () => (
          <SortableHeader
            label="Submitted"
            binding={CLAIMS_SORT_COLUMNS.createdAt}
            currentSort={values.sort}
            onSort={setSort}
          />
        ),
        cell: ({ row }) => (
          <span title={formatDate(row.original.claim.createdAt, { withTime: true })}>
            {timeAgo(row.original.claim.createdAt)}
          </span>
        ),
      },
      {
        id: 'student',
        accessorFn: (r) => r.student.name,
        enableSorting: false,
        header: 'Student',
        cell: ({ row }) => (
          <div className="min-w-0">
            <div className="font-medium truncate">{row.original.student.name}</div>
            {row.original.student.admissionNumber ? (
              <div className="text-xs text-[color:var(--color-text-muted)] truncate">
                {row.original.student.admissionNumber}
              </div>
            ) : null}
          </div>
        ),
      },
      {
        id: 'amount',
        accessorFn: (r) => r.payment.netAmountPaise,
        enableSorting: false,
        header: 'Amount',
        cell: ({ row }) => (
          <span className="font-medium">
            {formatINR(row.original.payment.netAmountPaise)}
          </span>
        ),
      },
      {
        id: 'paidVia',
        header: 'Paid via',
        enableSorting: false,
        cell: ({ row }) =>
          VIA_LABELS[row.original.claim.claimedPaidVia] ?? row.original.claim.claimedPaidVia,
      },
      {
        id: 'reference',
        header: 'Reference',
        enableSorting: false,
        cell: ({ row }) =>
          row.original.claim.reference ? (
            <span className="font-mono text-xs">{row.original.claim.reference}</span>
          ) : (
            <span className="text-xs text-[color:var(--color-text-muted)]">—</span>
          ),
      },
      {
        id: 'status',
        accessorFn: (r) => r.claim.status,
        enableSorting: false,
        header: 'Status',
        cell: ({ row }) => <ClaimStatusBadge status={row.original.claim.status} />,
      },
      {
        id: 'claimedBy',
        header: 'Claimed by',
        enableSorting: false,
        cell: ({ row }) => {
          const name = row.original.claim.claimedByName?.trim();
          return name && name.length > 0 ? (
            <span className="truncate">{name}</span>
          ) : (
            <span className="text-xs text-[color:var(--color-text-muted)]">—</span>
          );
        },
      },
      {
        id: 'pencil',
        header: '',
        enableSorting: false,
        cell: ({ row }) => {
          if (!isAdmin || row.original.claim.status !== 'pending') {
            return <span aria-hidden="true" />;
          }
          return (
            <button
              type="button"
              aria-label={`Edit claim from ${row.original.student.name}`}
              onClick={(e) => {
                e.stopPropagation();
                setEditClaim(row.original.claim);
              }}
              className={clsx(
                'inline-flex h-7 w-7 items-center justify-center rounded-md',
                'text-[color:var(--color-text-muted)] hover:bg-[color:var(--color-surface)] hover:text-[color:var(--color-text)]',
                'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gold-500',
              )}
            >
              <Pencil size={14} aria-hidden="true" />
            </button>
          );
        },
      },
      {
        id: 'actions',
        header: 'Action',
        enableSorting: false,
        cell: ({ row }) => {
          const s = row.original.claim.status;
          if (s !== 'pending') {
            return <span className="text-xs text-[color:var(--color-text-muted)]">—</span>;
          }
          return (
            <div className="flex items-center gap-2">
              <Button
                size="sm"
                onClick={(e) => {
                  e.stopPropagation();
                  void onConfirmClick(row.original);
                }}
                loading={
                  confirmMutation.isPending &&
                  confirmMutation.variables === row.original.claim.id
                }
              >
                Confirm
              </Button>
              <RowKebab
                onReject={() =>
                  setRejectTarget({
                    id: row.original.claim.id,
                    studentName: row.original.student.name,
                  })
                }
              />
            </div>
          );
        },
      },
    ],
    // confirmMutation pending state, isAdmin and the active sort all drive cell
    // render — all must stay in the dep array.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [confirmMutation.isPending, confirmMutation.variables, isAdmin, values.sort, setSort],
  );

  const onRowClick = (claim: FeeClaimJoined): void => {
    void navigate({
      to: '/finance/fees/claims/$claimId',
      params: { claimId: claim.claim.id },
    });
  };

  const items = claimsQuery.data?.items ?? [];
  const total = claimsQuery.data?.total ?? 0;
  const isLoadingList = claimsQuery.isLoading && !claimsQuery.data;

  return (
    <ListOrOutlet basePath="/finance/fees/claims">
    <ListShell
      title="Payments parents reported"
      description="Parents have reported these payments. Confirm to mark as received."
    >
      <FilterBar config={claimsFilterConfig} filters={filters} resultCount={total} />

      {claimsQuery.error ? (
        <ErrorState
          title="Could not load claims"
          description={
            claimsQuery.error instanceof ApiError
              ? claimsQuery.error.message
              : 'Please try again.'
          }
          onRetry={() => claimsQuery.refetch()}
        />
      ) : (
        <>
          <DataTable<FeeClaimJoined>
            data={items}
            columns={columns}
            isLoading={isLoadingList}
            getRowId={(r) => r.claim.id}
            onRowClick={onRowClick}
            emptyState={
              <EmptyState
                icon={<Inbox size={28} aria-hidden="true" />}
                title="No claims match these filters"
                description="Try clearing a filter. When parents report a payment, it will appear here to check."
              />
            }
          />
          <Pagination
            page={values.page}
            pageSize={PAGE_SIZE}
            total={total}
            onPageChange={setPage}
          />
        </>
      )}

      {isAdmin ? (
        <ClaimEditDrawer
          claim={editClaim}
          onClose={() => setEditClaim(null)}
          onSaved={() => setEditClaim(null)}
        />
      ) : null}

      {isAdmin ? (
        <ClaimRejectDrawer
          claimId={rejectTarget?.id ?? null}
          studentName={rejectTarget?.studentName ?? undefined}
          onClose={() => setRejectTarget(null)}
          onSuccess={() => setRejectTarget(null)}
        />
      ) : null}
    </ListShell>
    </ListOrOutlet>
  );
}

// ---------------------------------------------------------------------------
// Row-level kebab — only renders the Reject menu item for now, but keeping the
// shape consistent with other tables means we can grow it without churn.
// ---------------------------------------------------------------------------

interface RowKebabProps {
  onReject: () => void;
}

function RowKebab({ onReject }: RowKebabProps): JSX.Element {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent): void => {
      const target = e.target as HTMLElement | null;
      if (!target?.closest('[data-claim-row-kebab]')) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  return (
    <div className="relative" data-claim-row-kebab>
      <button
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label="More actions"
        onClick={(e) => {
          e.stopPropagation();
          setOpen((v) => !v);
        }}
        className={clsx(
          'inline-flex h-7 w-7 items-center justify-center rounded-md',
          'text-[color:var(--color-text-muted)] hover:bg-[color:var(--color-surface)] hover:text-[color:var(--color-text)]',
          'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gold-500',
        )}
      >
        <MoreHorizontal size={14} aria-hidden="true" />
      </button>
      {open ? (
        <div
          role="menu"
          className="absolute right-0 top-8 z-10 min-w-[140px] rounded-md border border-[color:var(--color-border)] bg-[color:var(--color-bg-elevated)] py-1 shadow-lg"
        >
          <button
            role="menuitem"
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              setOpen(false);
              onReject();
            }}
            className="block w-full px-3 py-2 text-left text-sm text-[color:var(--color-danger)] hover:bg-[color:var(--color-danger)]/10 focus:outline-none"
          >
            Reject claim
          </button>
        </div>
      ) : null}
    </div>
  );
}
